import React, { useContext, useRef, useState } from 'react';
import { videoStreamStore } from '../../../stores/videoContext';
import VerificationContainer from '../container/index.js';
import AllowCameraUseError from '../../cameraPermissionsError';
import { videoWrapperStyles } from '../../cameraPreview/styles';
import { useVerification } from '../../../stores/verificationContext';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@material-ui/core';
import CopyToClipboard from '../../copyToClipboard';
import Button from '@material-ui/core/Button';

type ReviewVideoRecordingProps = {
  next: Function;
  prev: Function;
};

export default function ReviewVideoRecording({
  next,
  prev,
}: ReviewVideoRecordingProps) {
  const { t, i18n } = useTranslation(['app']);

  const {
    documentPhotoBlob,
    documentBackPhotoBlob,
    selfiePhotoBlob,
    submitVerification,
  } = useVerification();

  const { videoStreamError, isCameraFacingUser, getRecordingBlob } = useContext(
    videoStreamStore
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [chromeIssue, setChromeIssue] = useState<boolean>(false);

  const videoBlob = getRecordingBlob();

  const goForward = () => {
    setLoading(true);
    submitVerification(
      documentPhotoBlob,
      documentBackPhotoBlob,
      selfiePhotoBlob,
      videoBlob,
      i18n.language
    )
      // Go to next screen
      .then((r) => {
        setLoading(false);
        next();
      })
      .catch((e) => {
        setLoading(false);
        // TODO: handle error
        console.error(e);
      });
  };

  const mediaStreamRef = useRef(null);

  const addMediaStream = (video: HTMLVideoElement) => {
    // Video stream should be already available from the previous step no need
    // handle errors here
    if (video) {
      if (videoBlob.size) {
        video.src = URL.createObjectURL(videoBlob);
      } else {
        setChromeIssue(true);
      }
    }
  };

  const classes = videoWrapperStyles();

  const goBack = () => {
    prev();
  };

  // Used to determine CSS classes
  const cameraFacingUser = isCameraFacingUser();

  if (chromeIssue) {
    return (
      <Grid container direction="column" justify="space-between">
        <Grid item>
          <Box ml={4} mr={4}>
            <Typography component="div" variant={'h5'} align={'center'}>
              <Box fontWeight="fontWeightBold">
                {'Issues with your browser'}
              </Box>
            </Typography>
            <br />
            <Typography align={'center'}>
              The current version of chrome has some issues
            </Typography>
            <br />
            <Typography align={'center'}>
              Please copy the URL below, open Firefox, Safari or any other
              browser and paste it into the address bar to continue
            </Typography>
            <br />
          </Box>
        </Grid>
        <Grid container item xs={12} justify={'center'}>
          <Grid item xs={12} sm={8}>
            <Box p={3}>
              <CopyToClipboard>
                {({ copy }) => (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => copy(window.location.href)}
                    fullWidth
                  >
                    Copy URL
                  </Button>
                )}
              </CopyToClipboard>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    );
  }
  return (
    <VerificationContainer
      next={goForward}
      prev={goBack}
      headerText={t('app:livelinessVerification', 'Liveliness Verification')}
      buttonText={t('app:yesFinish', 'Yes, finish')}
      prevButtonText={t('app:noRetake', 'No, retake')}
      buttonDisabled={false}
      loading={loading}
      stretch
    >
      {videoStreamError && <AllowCameraUseError />}
      <div
        className={`${classes.videoWrapperSelfieWithDoc}`}
        hidden={!videoBlob.size}
      >
        <video
          width={'100%'}
          height={'100%'}
          playsInline
          autoPlay
          loop
          ref={mediaStreamRef ? addMediaStream : null}
          className={clsx(classes.video, {
            [classes.mirrorVideo]: cameraFacingUser,
          })}
        />
      </div>
      <Grid alignItems={'center'} alignContent={'center'}>
        <Typography>
          {t(
            'app:isYourFaceAndIdDocumentFullyAndClearlyVisible',
            'Is your face and ID document fully and clearly visible ?'
          )}
        </Typography>
      </Grid>
    </VerificationContainer>
  );
}
